import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Divider,
  Toolbar,
  Stack,
  Skeleton,
  Fade,
  Slide,
  Grow,
  Zoom,
  useMediaQuery,
  keyframes
} from '@mui/material';
import { 
  Add, 
  Edit, 
  Delete, 
  AdminPanelSettings, 
  Person, 
  CheckCircle,
  Cancel,
  LockReset,
  Group, // manage_users
  Archive, // archive_content
  Send, // send_messages
  Schedule, // schedule_posts
  Assessment, // view_reports
  Telegram, // telegram_accounts
  Message, // manage_messages
  Public, // manage_proxy
  Campaign, // campaigns
  DataThresholding, // scraper
  ListAlt, // logger
  PeopleAlt // members_scraper
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ApiService from '../../API/Requesthandler';
import { motion, AnimatePresence } from 'framer-motion';

const cardVariants = {
  offscreen: { y: 20, opacity: 0 },
  onscreen: { 
    y: 0, 
    opacity: 1,
    transition: { type: 'spring', bounce: 0.4, duration: 0.8 }
  },
  exit: { opacity: 0, transition: { duration: 0.3 } }
};

const permissionIcons = {
  manage_users: <Group fontSize="small" />,
  archive_content: <Archive fontSize="small" />,
  send_messages: <Send fontSize="small" />,
  schedule_posts: <Schedule fontSize="small" />,
  view_reports: <Assessment fontSize="small" />,
  telegram_accounts: <Telegram fontSize="small" />,
  manage_messages: <Message fontSize="small" />,
  manage_proxy: <Public fontSize="small" />,
  campaigns: <Campaign fontSize="small" />,
  scraper: <DataThresholding fontSize="small" />,
  logger: <ListAlt fontSize="small" />,
  members_scraper: <PeopleAlt fontSize="small" />
};

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const skeletonStyle = {
  background: `linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)`,
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.5s infinite linear`,
};

function UserCard({ user, onEdit, onDelete, index }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <motion.div
      variants={cardVariants}
      initial="offscreen"
      animate="onscreen"
      exit="exit"
      viewport={{ once: true, amount: 0.2 }}
    >
      <Card sx={{ 
        height: '100%',
        transition: 'all 0.3s',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(195deg, rgba(33,33,33,1) 0%, rgba(18,18,18,1) 100%)' 
          : 'linear-gradient(195deg, rgba(255,255,255,1) 0%, rgba(245,245,245,1) 100%)',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: theme.shadows[6]
        }
      }}>
        <CardHeader
          avatar={
            <Avatar 
              sx={{ 
                bgcolor: user.role === 'admin' 
                  ? theme.palette.primary.main 
                  : theme.palette.secondary.main,
                width: 42,
                height: 42,
                boxShadow: theme.shadows[2]
              }}
            >
              {user.role === 'admin' ? (
                <AdminPanelSettings fontSize="small" />
              ) : (
                <Person fontSize="small" />
              )}
            </Avatar>
          }
          action={
            <Box sx={{ opacity: 0.8, transition: 'opacity 0.3s', '&:hover': { opacity: 1 } }}>
              <IconButton onClick={() => onEdit(user)} size="small">
                <Edit fontSize="small" />
              </IconButton>
              <IconButton onClick={() => onDelete(user)} size="small" color="error">
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          }
          title={
            <Typography variant="subtitle1" fontWeight="600" noWrap>
              {user.name}
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary" noWrap>
              {user.email}
            </Typography>
          }
          sx={{ pb: 0 }}
        />
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={user.role}
                size="small"
                color={user.role === 'admin' ? 'primary' : 'secondary'}
                variant="filled"
                sx={{ 
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  borderRadius: 1
                }}
              />
              <Chip
                label={user.status}
                size="small"
                color={user.status === 'active' ? 'success' : 'error'}
                icon={user.status === 'active' ? <CheckCircle /> : <Cancel />}
                sx={{ borderRadius: 1 }}
              />
            </Box>

            {/* <Divider sx={{ my: 1 }} /> */}

            {/* <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="500">
                LAST ACTIVITY
              </Typography>
              <Typography variant="body2" color="text.primary" fontWeight="500">
                {new Date(user.lastActivity).toLocaleString()}
              </Typography>
            </Box> */}

            <Divider sx={{ my: 1 }} />

            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="500">
                PERMISSIONS
              </Typography>
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 1.5,
                mt: 1.5,
                maxHeight: 200,
                overflow: 'auto',
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': { 
                  backgroundColor: theme.palette.divider,
                  borderRadius: 3
                }
              }}>
                {user.permissions.map((perm) => (
                  <Box
                    key={perm}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(0,0,0,0.05)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[1],
                        bgcolor: theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.08)'
                          : 'rgba(0,0,0,0.08)'
                      }
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: theme.palette.primary.main + '22',
                      color: theme.palette.primary.main
                    }}>
                      {permissionIcons[perm]}
                    </Box>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      lineHeight: 1.2,
                      textTransform: 'capitalize'
                    }}>
                      {perm.replace(/_/g, ' ')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function UserManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [state, setState] = useState({
    users: [],
    openDialog: false,
    selectedUser: null,
    roleFilter: 'all',
    snackbar: { open: false, message: '', severity: 'success' },
    newUser: { name: '', email: '', role: 'operator', permissions: [] },
    isLoading: false,
    isLoadingUsers: true,
    password: ''
  });

  

  const permissionsList = [
    'manage_users',
    'archive_content',
    'send_messages',
    'schedule_posts',
    'view_reports',
    'telegram_accounts',
    'manage_messages',
    'manage_proxy',
    'campaigns',
    'scraper',
    'logger',
    'members_scraper'
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = () => {
    setState(prev => ({ ...prev, isLoadingUsers: true }));
    ApiService.get('/api/users/')
      .then((res) => {
        const formattedUsers = res.map((user) => ({
          id: user.id,
          name: user.username,
          email: user.email,
          role: user.role,
          status: 'active',
          lastActivity: new Date().toISOString(),
          permissions: user.permissions || [],
        }));
        setTimeout(() => {
          setState(prev => ({
            ...prev,
            users: formattedUsers,
            isLoadingUsers: false,
            snackbar: { open: true, message: 'Successfully fetched users', severity: 'success' }
          }));
        }, 1000);
      })
      .catch((err) => {
        setState(prev => ({
          ...prev,
          snackbar: { open: true, message: 'Failed to fetch users', severity: 'error' },
          isLoadingUsers: false
        }));
      });
  };

  const handleAddUser = () => {
    setState(prev => ({
      ...prev,
      newUser: { name: '', email: '', role: 'operator', permissions: [] },
      password: '',
      selectedUser: null,
      openDialog: true
    }));
  };

  const handleEditUser = (user) => {
    setState(prev => ({ ...prev, selectedUser: user, openDialog: true }));
  };

  const handlerDeleteUser = (user) => {
    ApiService.delete(`/api/users/delete/${user.id}`)
      .then(res => {
        if (res.success) {
          fetchUsers();
          setState(prev => ({
            ...prev,
            snackbar: { open: true, message: 'User removed successfully', severity: 'success' }
          }));
        }
      })
      .catch(err => {
        setState(prev => ({
          ...prev,
          snackbar: { open: true, message: 'Error removing user', severity: 'error' }
        }))});
  };

  const handleSubmit = async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      if (state.selectedUser) {
        await ApiService.put(`/api/users/update/${state.selectedUser.id}`, {
          username: state.selectedUser.name,
          email: state.selectedUser.email,
          role: state.selectedUser.role,
          permissions: state.selectedUser.permissions,
          password: state.password
        });
        
        // Update local storage if needed
        const storage = JSON.parse(localStorage.getItem('user-data'));
        if (storage?.id === state.selectedUser.id) {
          storage.permissions = state.selectedUser.permissions;
          localStorage.setItem('user-data', JSON.stringify(storage));
        }
      } else {
        await ApiService.post('/api/users/add', {
          username: state.newUser.name,
          email: state.newUser.email,
          role: state.newUser.role,
          permissions: state.newUser.permissions,
          password: state.password
        });
      }
      
      setState(prev => ({
        ...prev,
        openDialog: false,
        snackbar: { 
          open: true, 
          message: `User ${state.selectedUser ? 'updated' : 'created'} successfully`, 
          severity: 'success' 
        },
        isLoading: false
      }));
      fetchUsers();
    } catch (error) {
      setState(prev => ({
        ...prev,
        snackbar: { open: true, message: 'Operation failed', severity: 'error' },
        isLoading: false
      }));
    }
  };

  const filteredUsers = state.users.filter(user =>
    state.roleFilter === 'all' ? true : user.role === state.roleFilter
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Toolbar disableGutters sx={{ mb: 4, gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddUser}
          sx={{
            // px: 4,
            // py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            // background: 'linear-gradient(195deg, #49a3f1, #1A73E8)',
            '&:hover': { transform: 'translateY(-1px)' },
            transition: 'all 0.3s'
          }}
        >
          Add User
        </Button>
        
        <FormControl size="small" sx={{ minWidth: 140, ml: 'auto' }}>
          <InputLabel>Filter by Role</InputLabel>
          <Select
            value={state.roleFilter}
            onChange={(e) => setState(prev => ({ ...prev, roleFilter: e.target.value }))}
            label="Filter by Role"
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="all">All Roles</MenuItem>
            <MenuItem value="admin">Admins</MenuItem>
            <MenuItem value="operator">Operators</MenuItem>
            <MenuItem value="analyst">Analysts</MenuItem>
          </Select>
        </FormControl>
      </Toolbar>

      <Grid container spacing={3}>
        {state.isLoadingUsers ? (
          [...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ height: '100%', overflow: 'hidden' }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Skeleton 
                      variant="rectangular" 
                      width="60%" 
                      height={24} 
                      sx={{ ...skeletonStyle, borderRadius: 2 }}
                    />
                    <Skeleton 
                      variant="rectangular" 
                      width="80%" 
                      height={18} 
                      sx={{ ...skeletonStyle, borderRadius: 2 }}
                    />
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      <Skeleton 
                        variant="circular" 
                        width={32} 
                        height={32} 
                        sx={skeletonStyle}
                      />
                      <Skeleton 
                        variant="circular" 
                        width={32} 
                        height={32} 
                        sx={skeletonStyle}
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <AnimatePresence initial={false}>
            {filteredUsers.map((user, index) => (
              <Grid item xs={12} sm={6} md={4} key={user.id}>
                <UserCard 
                  user={user}
                  onEdit={handleEditUser}
                  onDelete={handlerDeleteUser}
                  index={index}
                />
              </Grid>
            ))}
          </AnimatePresence>
        )}
      </Grid>

      <Dialog
        open={state.openDialog}
        onClose={() => setState(prev => ({ ...prev, openDialog: false }))}
        maxWidth="md"
        // sx={{borderRadius: 3}}
        fullWidth
        TransitionComponent={Slide}
      >
        <Box sx={{ 
          background: theme.palette.background.paper,
          // borderRadius: 3,
          overflow: 'hidden'
        }}>
          <DialogTitle sx={{ 
            // bgcolor: 'primary.main', 
            // color: 'primary.contrastText',
            fontWeight: 600,
            py: 2,
            marginBottom:2
          }}>
            {state.selectedUser ? 'Edit User' : 'Create New User'}
          </DialogTitle>
          
          <DialogContent sx={{ py: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={state.selectedUser ? state.selectedUser.name : state.newUser.name}
                  onChange={(e) => setState(prev => ({
                    ...prev,
                    selectedUser: prev.selectedUser 
                      ? { ...prev.selectedUser, name: e.target.value }
                      : null,
                    newUser: !prev.selectedUser 
                      ? { ...prev.newUser, name: e.target.value }
                      : prev.newUser
                  }))}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={state.selectedUser ? state.selectedUser.email : state.newUser.email}
                  onChange={(e) => setState(prev => ({
                    ...prev,
                    selectedUser: prev.selectedUser 
                      ? { ...prev.selectedUser, email: e.target.value }
                      : null,
                    newUser: !prev.selectedUser 
                      ? { ...prev.newUser, email: e.target.value }
                      : prev.newUser
                  }))}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={state.password}
                  onChange={(e) => setState(prev => ({ ...prev, password: e.target.value }))}
                  placeholder={state.selectedUser ? 'Leave blank to keep current' : ''}
                  InputProps={{
                    endAdornment: <LockReset sx={{ color: 'action.active' }} />
                  }}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={state.selectedUser ? state.selectedUser.role : state.newUser.role}
                    onChange={(e) => setState(prev => ({
                      ...prev,
                      selectedUser: prev.selectedUser 
                        ? { ...prev.selectedUser, role: e.target.value }
                        : null,
                      newUser: !prev.selectedUser 
                        ? { ...prev.newUser, role: e.target.value }
                        : prev.newUser
                    }))}
                    label="Role"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="operator">Operator</MenuItem>
                    <MenuItem value="analyst">Analyst</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  Permissions
                </Typography>
                <Grid container spacing={2}>
                  {permissionsList.map((permission) => (
                    <Grid item xs={6} sm={4} md={3} key={permission}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              state.selectedUser 
                                ? state.selectedUser.permissions.includes(permission)
                                : state.newUser.permissions.includes(permission)
                            }
                            onChange={(e) => {
                              const currentPermissions = state.selectedUser 
                                ? state.selectedUser.permissions 
                                : state.newUser.permissions;
                              const newPermissions = e.target.checked
                                ? [...currentPermissions, permission]
                                : currentPermissions.filter(p => p !== permission);
                              
                              setState(prev => ({
                                ...prev,
                                selectedUser: prev.selectedUser 
                                  ? { ...prev.selectedUser, permissions: newPermissions }
                                  : null,
                                newUser: !prev.selectedUser 
                                  ? { ...prev.newUser, permissions: newPermissions }
                                  : prev.newUser
                              }));
                            }}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {permission.replace(/_/g, ' ')}
                          </Typography>
                        }
                        sx={{
                          m: 0,
                          width: '100%',
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          p: 1,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ px: 4, py: 2 }}>
            <Button 
              onClick={() => setState(prev => ({ ...prev, openDialog: false }))}
              sx={{ borderRadius: 2, px: 4 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={state.isLoading}
              sx={{
                borderRadius: 2,
                px: 4,
                // background: 'linear-gradient(195deg, #49a3f1, #1A73E8)',
                '&:disabled': { opacity: 0.7 }
              }}
            >
              {state.isLoading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : state.selectedUser ? 'Update User' : 'Create User'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Snackbar
        open={state.snackbar.open}
        autoHideDuration={4000}
        onClose={() => setState(prev => ({ ...prev, snackbar: { ...prev.snackbar, open: false } }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          severity={state.snackbar.severity}
          sx={{
            width: '100%',
            boxShadow: 3,
            borderRadius: 2,
            alignItems: 'center',
            fontWeight: 500
          }}
        >
          {state.snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}